import Papa from 'papaparse';
import * as XLSX from 'xlsx';


export const LOAN_FIELDS = [
    { key: 'borrower', label: 'Borrower Name', required: true, type: 'string', description: 'Company or entity name' },
    { key: 'amount', label: 'Loan Amount', required: true, type: 'number', description: 'Principal amount' },
    { key: 'currency', label: 'Currency', required: false, type: 'string', default: 'EUR', description: 'ISO currency code' },
    { key: 'leadArranger', label: 'Lead Arranger', required: true, type: 'string', description: 'Lead bank/arranger' },
    { key: 'sector', label: 'Sector', required: false, type: 'string', description: 'Industry sector' },
    { key: 'country', label: 'Country', required: false, type: 'string', description: 'Country of operation' },
    { key: 'startDate', label: 'Start Date', required: false, type: 'date', description: 'Loan inception date' },
    { key: 'maturityDate', label: 'Maturity Date', required: true, type: 'date', description: 'Loan maturity date' },
    { key: 'nextReportingDate', label: 'Next Reporting Date', required: false, type: 'date', description: 'Next compliance reporting date' },
    { key: 'reference', label: 'Reference', required: false, type: 'string', description: 'Internal reference number' },
];


export const COVENANT_FIELDS = [
    { key: 'loanId', label: 'Loan ID / Borrower', required: true, type: 'string', description: 'Loan identifier or borrower name' },
    { key: 'name', label: 'Covenant Name', required: true, type: 'string', description: 'Name of the covenant (e.g., Debt/EBITDA)' },
    { key: 'threshold', label: 'Threshold Value', required: true, type: 'number', description: 'Threshold/limit value' },
    { key: 'operator', label: 'Operator (<=, >=, =)', required: true, type: 'operator', description: 'Comparison operator' },
    { key: 'current', label: 'Current Value', required: true, type: 'number', description: 'Current measured value' },
    { key: 'frequency', label: 'Frequency', required: false, type: 'string', default: 'Quarterly', description: 'Reporting frequency' },
];


export const FINANCIAL_UPDATE_FIELDS = [
    { key: 'borrower', label: 'Borrower Name', required: true, type: 'string', description: 'Company name to match' },
    { key: 'reportingDate', label: 'Reporting Date', required: true, type: 'date', description: 'Date of financial data' },
    { key: 'covenantName', label: 'Covenant Name', required: true, type: 'string', description: 'Covenant to update' },
    { key: 'value', label: 'New Value', required: true, type: 'number', description: 'New covenant value' },
    { key: 'notes', label: 'Notes', required: false, type: 'string', description: 'Optional notes/comments' },
];


const FIELD_ALIASES = {
    borrower: ['company', 'client', 'name', 'emprunteur', 'société', 'societe', 'entity', 'counterparty', 'obligor', 'borrowername', 'companyname'],
    amount: ['principal', 'value', 'montant', 'loan', 'facility', 'commitment', 'loanamount', 'facilityamount', 'size'],
    currency: ['ccy', 'devise', 'curr', 'iso'],
    leadArranger: ['arranger', 'bank', 'lender', 'arrangeur', 'banque', 'agent', 'bookrunner', 'lead', 'mandatedlead'],
    sector: ['industry', 'secteur', 'industrie', 'segment', 'vertical'],
    country: ['pays', 'location', 'region', 'jurisdiction', 'geography', 'domicile'],
    startDate: ['start', 'inception', 'debut', 'début', 'effective', 'signing', 'closing', 'startdate', 'effectivedate'],
    maturityDate: ['maturity', 'end', 'expiry', 'échéance', 'echeance', 'fin', 'termination', 'final', 'maturitydate', 'enddate'],
    nextReportingDate: ['reporting', 'nextreport', 'compliance', 'test', 'nexttest'],
    reference: ['ref', 'id', 'number', 'dealid', 'loanid', 'facilityid'],
    threshold: ['limit', 'max', 'min', 'seuil', 'limite', 'target', 'covenant', 'ratio'],
    current: ['actual', 'value', 'actuel', 'valeur', 'currentvalue', 'measured', 'result'],
    operator: ['op', 'comparison', 'test', 'condition', 'type'],
    covenantName: ['covenant', 'ratio', 'metric', 'kpi', 'indicator', 'measure'],
    name: ['covenant', 'covenantname', 'rationame', 'metric'],
    loanId: ['loan', 'loanid', 'borrower', 'facility', 'dealid'],
    reportingDate: ['date', 'period', 'quarter', 'testdate', 'asof'],
    value: ['result', 'actual', 'measured', 'newvalue'],
    notes: ['comment', 'remarks', 'note', 'description'],
    frequency: ['freq', 'period', 'schedule', 'testing'],
};


export const parseCSV = (file, options = {}) => {
    return new Promise((resolve, reject) => {
        const parseOptions = {
            header: true,
            skipEmptyLines: 'greedy',
            dynamicTyping: false,
            delimitersToGuess: [',', ';', '\t', '|'],
            transformHeader: (header) => header.trim(),
            encoding: options.encoding || 'UTF-8',
            ...options,
        };

        Papa.parse(file, {
            ...parseOptions,
            complete: (results) => {
                const headers = results.meta.fields || [];
                const data = results.data
                    .filter(row => Object.values(row).some(v => v !== '' && v !== null && v !== undefined))
                    .map((row, index) => ({
                        _rowIndex: index + 2,
                        ...row,
                    }));

                const errors = results.errors.map(
                    (err) => `Row ${err.row + 2}: ${err.message}`
                );

                const stats = {
                    totalRows: results.data.length,
                    validRows: data.length,
                    emptyRows: results.data.length - data.length,
                    columns: headers.length,
                    delimiter: results.meta.delimiter,
                };

                resolve({ headers, data, errors, stats });
            },
            error: (error) => {
                reject(new Error(`CSV parsing failed: ${error.message}`));
            },
        });
    });
};


export const parseExcel = (file, options = {}) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, {
                    type: 'array',
                    cellDates: true,
                    cellNF: true,
                    cellStyles: false,
                });

                const sheetNames = workbook.SheetNames;
                const targetSheet = options.sheetName || sheetNames[0];
                const worksheet = workbook.Sheets[targetSheet];

                if (!worksheet) {
                    reject(new Error(`Sheet "${targetSheet}" not found. Available: ${sheetNames.join(', ')}`));
                    return;
                }


                const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
                const headerRow = options.headerRow || 0;


                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: '',
                    blankrows: false,
                    raw: false,
                    dateNF: 'yyyy-mm-dd',
                });

                if (jsonData.length <= headerRow) {
                    resolve({ headers: [], data: [], sheetNames, errors: ['Empty or insufficient data'], stats: {} });
                    return;
                }


                const headers = jsonData[headerRow]
                    .map((h, i) => h ? String(h).trim() : `Column_${i + 1}`)
                    .filter(h => h);


                const rows = jsonData.slice(headerRow + 1)
                    .filter(row => row.some(cell => cell !== '' && cell !== null && cell !== undefined))
                    .map((row, index) => {
                        const obj = { _rowIndex: index + headerRow + 3 };
                        headers.forEach((header, colIndex) => {
                            let value = row[colIndex];

                            if (typeof value === 'number' && value > 25569 && value < 50000) {
                                const date = new Date((value - 25569) * 86400 * 1000);
                                value = date.toISOString().split('T')[0];
                            }
                            obj[header] = value !== undefined ? value : '';
                        });
                        return obj;
                    });

                const stats = {
                    totalRows: jsonData.length - 1,
                    validRows: rows.length,
                    emptyRows: jsonData.length - 1 - rows.length,
                    columns: headers.length,
                    sheets: sheetNames.length,
                    activeSheet: targetSheet,
                };

                resolve({
                    headers,
                    data: rows,
                    sheetNames,
                    errors: [],
                    stats,
                });
            } catch (error) {
                reject(new Error(`Excel parsing failed: ${error.message}`));
            }
        };

        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };

        reader.readAsArrayBuffer(file);
    });
};


export const parseFile = async (file, options = {}) => {
    const extension = file.name.split('.').pop().toLowerCase();

    if (extension === 'csv' || extension === 'txt') {
        return parseCSV(file, options);
    } else if (['xlsx', 'xls', 'xlsm'].includes(extension)) {
        return parseExcel(file, options);
    } else {
        throw new Error(`Unsupported file format: .${extension}. Supported: CSV, XLSX, XLS`);
    }
};


const stringSimilarity = (str1, str2) => {
    const s1 = str1.toLowerCase().replace(/[^a-z0-9]/g, '');
    const s2 = str2.toLowerCase().replace(/[^a-z0-9]/g, '');

    if (s1 === s2) return 1;
    if (s1.includes(s2) || s2.includes(s1)) return 0.8;


    let matches = 0;
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;

    for (let i = 0; i < shorter.length; i++) {
        if (longer.includes(shorter[i])) matches++;
    }

    return matches / longer.length;
};


export const autoMapHeaders = (headers, expectedFields) => {
    const mapping = {};
    const usedHeaders = new Set();
    const normalizedHeaders = headers.map((h) => ({
        original: h,
        normalized: h.toLowerCase().replace(/[_\s\-\.]/g, ''),
    }));


    const sortedFields = [...expectedFields].sort((a, b) => {
        if (a.required !== b.required) return b.required - a.required;
        return 0;
    });

    sortedFields.forEach((field) => {
        const normalizedKey = field.key.toLowerCase();
        const normalizedLabel = field.label.toLowerCase().replace(/[_\s\-\.]/g, '');
        const aliases = FIELD_ALIASES[field.key] || [];

        let bestMatch = null;
        let bestScore = 0;

        normalizedHeaders.forEach(({ original, normalized }) => {
            if (usedHeaders.has(original)) return;

            let score = 0;


            if (normalized === normalizedKey) {
                score = 1.0;
            }

            else if (normalized === normalizedLabel) {
                score = 0.95;
            }

            else if (normalized.includes(normalizedKey) || normalizedKey.includes(normalized)) {
                score = 0.85;
            }

            else {
                for (const alias of aliases) {
                    const normAlias = alias.toLowerCase().replace(/[_\s\-\.]/g, '');
                    if (normalized === normAlias) {
                        score = 0.9;
                        break;
                    } else if (normalized.includes(normAlias) || normAlias.includes(normalized)) {
                        score = Math.max(score, 0.75);
                    }
                }
            }


            if (score === 0) {
                score = stringSimilarity(normalized, normalizedKey) * 0.5;
            }

            if (score > bestScore && score >= 0.5) {
                bestScore = score;
                bestMatch = original;
            }
        });

        if (bestMatch) {
            mapping[field.key] = bestMatch;
            usedHeaders.add(bestMatch);
        }
    });

    return mapping;
};


export const getMappingConfidence = (headers, expectedFields, mapping) => {
    const confidence = {};

    expectedFields.forEach(field => {
        if (mapping[field.key]) {
            const header = mapping[field.key].toLowerCase().replace(/[_\s\-\.]/g, '');
            const key = field.key.toLowerCase();
            const aliases = FIELD_ALIASES[field.key] || [];

            if (header === key) {
                confidence[field.key] = { score: 100, label: 'Exact match' };
            } else if (aliases.some(a => header.includes(a.toLowerCase()))) {
                confidence[field.key] = { score: 90, label: 'Alias match' };
            } else if (header.includes(key) || key.includes(header)) {
                confidence[field.key] = { score: 80, label: 'Partial match' };
            } else {
                confidence[field.key] = { score: 60, label: 'Fuzzy match' };
            }
        } else {
            confidence[field.key] = { score: 0, label: 'Not mapped' };
        }
    });

    return confidence;
};


export const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return NaN;
    if (typeof value === 'number') return value;

    let str = String(value).trim();


    str = str.replace(/[€$£¥₹\s]/g, '');


    const isPercentage = str.endsWith('%');
    if (isPercentage) {
        str = str.slice(0, -1);
    }


    const multipliers = {
        'k': 1_000,
        'K': 1_000,
        'm': 1_000_000,
        'M': 1_000_000,
        'mm': 1_000_000,
        'MM': 1_000_000,
        'b': 1_000_000_000,
        'B': 1_000_000_000,
        'bn': 1_000_000_000,
        'BN': 1_000_000_000,
        't': 1_000_000_000_000,
        'T': 1_000_000_000_000,
    };

    let multiplier = 1;
    for (const [suffix, mult] of Object.entries(multipliers)) {
        if (str.endsWith(suffix)) {
            multiplier = mult;
            str = str.slice(0, -suffix.length);
            break;
        }
    }


    if (str.startsWith('(') && str.endsWith(')')) {
        str = '-' + str.slice(1, -1);
    }


    const hasComma = str.includes(',');
    const hasDot = str.includes('.');

    if (hasComma && hasDot) {

        const lastComma = str.lastIndexOf(',');
        const lastDot = str.lastIndexOf('.');

        if (lastComma > lastDot) {

            str = str.replace(/\./g, '').replace(',', '.');
        } else {

            str = str.replace(/,/g, '');
        }
    } else if (hasComma) {

        const afterComma = str.split(',')[1];
        if (afterComma && afterComma.length <= 2 && !afterComma.includes(',')) {

            str = str.replace(',', '.');
        } else {

            str = str.replace(/,/g, '');
        }
    }

    else if (hasDot) {
        const dots = str.split('.').length - 1;
        if (dots > 1) {
            str = str.replace(/\./g, '');
        }
    }

    const parsed = parseFloat(str) * multiplier;

    if (isPercentage) {
        return parsed / 100;
    }

    return parsed;
};


export const parseDate = (value) => {
    if (!value) return null;


    if (value instanceof Date) {
        if (isNaN(value.getTime())) return null;
        return value.toISOString().split('T')[0];
    }


    if (typeof value === 'number' && value > 0 && value < 100000) {
        const date = new Date((value - 25569) * 86400 * 1000);
        if (!isNaN(date.getTime())) {
            return date.toISOString().split('T')[0];
        }
    }

    const dateStr = String(value).trim();


    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
            return dateStr.split('T')[0];
        }
    }


    const euMatch = dateStr.match(/^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{2,4})$/);
    if (euMatch) {
        let [, day, month, year] = euMatch;

        if (year.length === 2) {
            year = parseInt(year) > 50 ? '19' + year : '20' + year;
        }

        const d = parseInt(day), m = parseInt(month);
        if (d >= 1 && d <= 31 && m >= 1 && m <= 12) {
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
    }


    const usMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (usMatch) {
        let [, month, day, year] = usMatch;
        if (year.length === 2) {
            year = parseInt(year) > 50 ? '19' + year : '20' + year;
        }
        const d = parseInt(day), m = parseInt(month);

        if (m > 12 && d <= 12) {
            [day, month] = [month, day];
        }
        if (parseInt(month) >= 1 && parseInt(month) <= 12 && parseInt(day) >= 1 && parseInt(day) <= 31) {
            return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
    }


    const months = {
        'jan': '01', 'january': '01', 'janvier': '01',
        'feb': '02', 'february': '02', 'février': '02', 'fevrier': '02',
        'mar': '03', 'march': '03', 'mars': '03',
        'apr': '04', 'april': '04', 'avril': '04',
        'may': '05', 'mai': '05',
        'jun': '06', 'june': '06', 'juin': '06',
        'jul': '07', 'july': '07', 'juillet': '07',
        'aug': '08', 'august': '08', 'août': '08', 'aout': '08',
        'sep': '09', 'sept': '09', 'september': '09', 'septembre': '09',
        'oct': '10', 'october': '10', 'octobre': '10',
        'nov': '11', 'november': '11', 'novembre': '11',
        'dec': '12', 'december': '12', 'décembre': '12', 'decembre': '12',
    };

    const textMatch = dateStr.match(/(\d{1,2})[,\s]+([a-zA-Zéû]+)[,\s]+(\d{4})/i)
        || dateStr.match(/([a-zA-Zéû]+)[,\s]+(\d{1,2})[,\s]+(\d{4})/i);

    if (textMatch) {
        let day, monthStr, year;
        if (/^\d+$/.test(textMatch[1])) {
            [, day, monthStr, year] = textMatch;
        } else {
            [, monthStr, day, year] = textMatch;
        }
        const month = months[monthStr.toLowerCase()];
        if (month) {
            return `${year}-${month}-${String(day).padStart(2, '0')}`;
        }
    }


    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
        return parsed.toISOString().split('T')[0];
    }

    return null;
};


const parseOperator = (value) => {
    if (!value) return null;
    const str = String(value).trim().toLowerCase();

    const operatorMap = {
        '<=': '<=', 'lte': '<=', 'le': '<=', '≤': '<=', 'max': '<=', 'maximum': '<=',
        '>=': '>=', 'gte': '>=', 'ge': '>=', '≥': '>=', 'min': '>=', 'minimum': '>=',
        '<': '<', 'lt': '<', 'less': '<',
        '>': '>', 'gt': '>', 'greater': '>',
        '=': '=', 'eq': '=', 'equal': '=', '==': '=',
    };

    return operatorMap[str] || null;
};


export const transformData = (data, mapping, expectedFields, options = {}) => {
    const transformedData = [];
    const errors = [];
    const warnings = [];
    const strict = options.strict || false;

    data.forEach((row) => {
        const transformedRow = { _rowIndex: row._rowIndex, _source: {} };
        let hasRequiredFields = true;
        let rowHasErrors = false;

        expectedFields.forEach((field) => {
            const sourceColumn = mapping[field.key];
            let rawValue = sourceColumn ? row[sourceColumn] : undefined;
            let value = rawValue;


            if (rawValue !== undefined) {
                transformedRow._source[field.key] = rawValue;
            }


            if (value === undefined || value === null || value === '') {
                value = field.default;
            }


            if (value !== undefined && value !== null && value !== '') {
                try {
                    switch (field.type) {
                        case 'number':
                            const numValue = parseNumber(value);
                            if (isNaN(numValue)) {
                                if (field.required || strict) {
                                    errors.push(`Row ${row._rowIndex}: Cannot parse "${rawValue}" as number for "${field.label}"`);
                                    rowHasErrors = true;
                                } else {
                                    warnings.push(`Row ${row._rowIndex}: Invalid number "${rawValue}" for "${field.label}", using null`);
                                }
                                value = null;
                            } else {
                                value = numValue;
                            }
                            break;

                        case 'date':
                            const dateValue = parseDate(value);
                            if (!dateValue) {
                                if (field.required || strict) {
                                    errors.push(`Row ${row._rowIndex}: Cannot parse "${rawValue}" as date for "${field.label}"`);
                                    rowHasErrors = true;
                                } else {
                                    warnings.push(`Row ${row._rowIndex}: Invalid date "${rawValue}" for "${field.label}", using null`);
                                }
                                value = null;
                            } else {
                                value = dateValue;
                            }
                            break;

                        case 'operator':
                            const opValue = parseOperator(value);
                            if (!opValue) {
                                if (field.required || strict) {
                                    errors.push(`Row ${row._rowIndex}: Invalid operator "${rawValue}" for "${field.label}". Use: <=, >=, <, >, =`);
                                    rowHasErrors = true;
                                }
                                value = null;
                            } else {
                                value = opValue;
                            }
                            break;

                        case 'string':
                        default:
                            value = String(value).trim();
                            break;
                    }
                } catch (e) {
                    errors.push(`Row ${row._rowIndex}: Error processing "${field.label}": ${e.message}`);
                    rowHasErrors = true;
                    value = null;
                }
            }


            if (field.required && (value === undefined || value === null || value === '')) {
                hasRequiredFields = false;
                errors.push(`Row ${row._rowIndex}: Missing required field "${field.label}"`);
            }

            transformedRow[field.key] = value;
        });

        if (hasRequiredFields && (!strict || !rowHasErrors)) {
            transformedData.push(transformedRow);
        }
    });

    return { transformedData, errors, warnings };
};


export const validateData = (data, importType, options = {}) => {
    const valid = [];
    const invalid = [];
    const errors = [];
    const warnings = [];

    data.forEach((row) => {
        const rowErrors = [];
        const rowWarnings = [];

        switch (importType) {
            case 'loans':

                if (!row.borrower || row.borrower.length < 2) {
                    rowErrors.push('Borrower name must be at least 2 characters');
                }
                if (!row.amount || row.amount <= 0) {
                    rowErrors.push('Loan amount must be positive');
                }
                if (row.amount && row.amount > 100_000_000_000) {
                    rowWarnings.push('Loan amount seems unusually large (>100B)');
                }
                if (!row.maturityDate) {
                    rowErrors.push('Maturity date is required');
                }
                if (!row.leadArranger) {
                    rowErrors.push('Lead arranger is required');
                }


                if (row.startDate && row.maturityDate) {
                    if (new Date(row.startDate) >= new Date(row.maturityDate)) {
                        rowErrors.push('Start date must be before maturity date');
                    }
                }
                if (row.maturityDate && new Date(row.maturityDate) < new Date()) {
                    rowWarnings.push('Maturity date is in the past');
                }


                if (row.currency && !/^[A-Z]{3}$/.test(row.currency.toUpperCase())) {
                    rowWarnings.push(`Currency "${row.currency}" may not be a valid ISO code`);
                    row.currency = row.currency.toUpperCase();
                }
                break;

            case 'covenants':
                if (!row.name || row.name.length < 2) {
                    rowErrors.push('Covenant name is required');
                }
                if (row.threshold === null || row.threshold === undefined) {
                    rowErrors.push('Threshold value is required');
                }
                if (!['<=', '>=', '=', '<', '>'].includes(row.operator)) {
                    rowErrors.push(`Invalid operator "${row.operator}". Use: <=, >=, <, >, =`);
                }
                if (row.current === null || row.current === undefined) {
                    rowErrors.push('Current value is required');
                }
                if (!row.loanId) {
                    rowErrors.push('Loan ID/Borrower is required');
                }


                if (row.current !== null && row.threshold !== null && row.operator) {
                    let isCompliant = false;
                    switch (row.operator) {
                        case '<=': isCompliant = row.current <= row.threshold; break;
                        case '>=': isCompliant = row.current >= row.threshold; break;
                        case '<': isCompliant = row.current < row.threshold; break;
                        case '>': isCompliant = row.current > row.threshold; break;
                        case '=': isCompliant = row.current === row.threshold; break;
                    }


                    const headroom = row.operator === '<=' || row.operator === '<'
                        ? (row.threshold - row.current) / Math.abs(row.threshold) * 100
                        : (row.current - row.threshold) / Math.abs(row.threshold) * 100;

                    row._status = isCompliant
                        ? (headroom < 10 ? 'warning' : 'ok')
                        : 'critical';
                    row._headroom = headroom.toFixed(1);
                }
                break;

            case 'financial':
                if (!row.borrower) {
                    rowErrors.push('Borrower name is required');
                }
                if (!row.covenantName) {
                    rowErrors.push('Covenant name is required');
                }
                if (row.value === null || row.value === undefined) {
                    rowErrors.push('Value is required');
                }
                if (!row.reportingDate) {
                    rowErrors.push('Reporting date is required');
                }
                if (row.reportingDate && new Date(row.reportingDate) > new Date()) {
                    rowWarnings.push('Reporting date is in the future');
                }
                break;
        }

        if (rowErrors.length > 0) {
            invalid.push({ ...row, _errors: rowErrors, _warnings: rowWarnings });
            errors.push(`Row ${row._rowIndex}: ${rowErrors.join('; ')}`);
        } else {
            if (rowWarnings.length > 0) {
                row._warnings = rowWarnings;
                warnings.push(`Row ${row._rowIndex}: ${rowWarnings.join('; ')}`);
            }
            valid.push(row);
        }
    });

    return { valid, invalid, errors, warnings };
};


export const generateTemplate = (importType, options = {}) => {
    const format = options.format || 'csv';
    const includeExamples = options.includeExamples !== false;

    let headers, examples;

    switch (importType) {
        case 'loans':
            headers = ['Borrower', 'Amount', 'Currency', 'Lead Arranger', 'Sector', 'Country', 'Start Date', 'Maturity Date', 'Next Reporting Date', 'Reference'];
            examples = [
                ['Acme Corp', '100000000', 'EUR', 'Deutsche Bank', 'Manufacturing', 'Germany', '2025-01-15', '2029-01-15', '2025-03-31', 'LOAN-001'],
                ['TechStart SA', '50M', 'EUR', 'BNP Paribas', 'Technology', 'France', '2025-06-01', '2027-06-01', '2025-06-30', 'LOAN-002'],
                ['Global Shipping Ltd', '€250.000.000', 'EUR', 'HSBC', 'Shipping', 'Greece', '2023-03-15', '2028-03-15', '2025-01-10', 'LOAN-003'],
            ];
            break;

        case 'covenants':
            headers = ['Loan ID / Borrower', 'Covenant Name', 'Threshold', 'Operator', 'Current Value', 'Frequency'];
            examples = [
                ['Acme Corp', 'Debt/EBITDA', '4.0', '<=', '3.5', 'Quarterly'],
                ['Acme Corp', 'Interest Coverage', '2.5', '>=', '3.2', 'Quarterly'],
                ['TechStart SA', 'Current Ratio', '1.2', '>=', '1.5', 'Semi-Annual'],
                ['Global Shipping Ltd', 'LTV Ratio', '0.75', '<=', '0.68', 'Quarterly'],
            ];
            break;

        case 'financial':
            headers = ['Borrower', 'Reporting Date', 'Covenant Name', 'Value', 'Notes'];
            examples = [
                ['Acme Corp', '2025-12-31', 'Debt/EBITDA', '3.8', 'Q4 2025 results'],
                ['Acme Corp', '2025-12-31', 'Interest Coverage', '2.9', 'Q4 2025 results'],
                ['TechStart SA', '2025-12-31', 'Current Ratio', '1.45', 'Year-end reporting'],
            ];
            break;

        default:
            throw new Error(`Unknown import type: ${importType}`);
    }

    if (format === 'xlsx') {

        const ws = XLSX.utils.aoa_to_sheet([headers, ...(includeExamples ? examples : [])]);


        ws['!cols'] = headers.map(() => ({ wch: 20 }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, importType.charAt(0).toUpperCase() + importType.slice(1));


        const instructions = [
            ['Import Template Instructions'],
            [''],
            ['1. Fill in your data starting from row 2'],
            ['2. Required fields are marked with an asterisk in the main application'],
            ['3. Date formats accepted: YYYY-MM-DD, DD/MM/YYYY, DD.MM.YYYY'],
            ['4. Number formats accepted: 1000000, 1.000.000, 1,000,000, 1M, 1B'],
            ['5. Currency: Use ISO codes (EUR, USD, GBP)'],
            ['6. Operators: <=, >=, <, >, ='],
        ];
        const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
        XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

        XLSX.writeFile(wb, `${importType}_template.xlsx`);
    } else {

        const rows = [headers];
        if (includeExamples) {
            rows.push(...examples);
        }

        const csv = rows.map(row => row.map(cell => {

            if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
                return `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
        }).join(',')).join('\n');

        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `${importType}_template.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }
};


export const previewTransform = (sampleRow, mapping, expectedFields) => {
    const preview = {};

    expectedFields.forEach(field => {
        const sourceColumn = mapping[field.key];
        const rawValue = sourceColumn ? sampleRow[sourceColumn] : null;

        let transformedValue = rawValue;
        let status = 'ok';

        if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
            switch (field.type) {
                case 'number':
                    transformedValue = parseNumber(rawValue);
                    if (isNaN(transformedValue)) {
                        status = 'error';
                        transformedValue = 'Invalid';
                    }
                    break;
                case 'date':
                    transformedValue = parseDate(rawValue);
                    if (!transformedValue) {
                        status = 'error';
                        transformedValue = 'Invalid';
                    }
                    break;
                case 'operator':
                    transformedValue = parseOperator(rawValue);
                    if (!transformedValue) {
                        status = 'error';
                        transformedValue = 'Invalid';
                    }
                    break;
            }
        } else if (field.required) {
            status = 'missing';
            transformedValue = field.default || 'Missing';
        } else {
            transformedValue = field.default || '-';
        }

        preview[field.key] = {
            raw: rawValue,
            transformed: transformedValue,
            status,
        };
    });

    return preview;
};


export const analyzeDataQuality = (data, mapping, expectedFields) => {
    const issues = {
        missingValues: {},
        invalidFormats: {},
        duplicates: [],
        outliers: [],
    };

    const seenBorrowers = new Map();
    const numericValues = {};

    expectedFields.forEach(field => {
        issues.missingValues[field.key] = 0;
        issues.invalidFormats[field.key] = 0;
        if (field.type === 'number') {
            numericValues[field.key] = [];
        }
    });

    data.forEach((row, index) => {
        expectedFields.forEach(field => {
            const sourceColumn = mapping[field.key];
            const value = sourceColumn ? row[sourceColumn] : null;

            if (value === null || value === undefined || value === '') {
                issues.missingValues[field.key]++;
            } else if (field.type === 'number') {
                const parsed = parseNumber(value);
                if (isNaN(parsed)) {
                    issues.invalidFormats[field.key]++;
                } else {
                    numericValues[field.key].push({ value: parsed, row: index + 2 });
                }
            } else if (field.type === 'date') {
                if (!parseDate(value)) {
                    issues.invalidFormats[field.key]++;
                }
            }
        });


        const borrowerCol = mapping['borrower'];
        if (borrowerCol && row[borrowerCol]) {
            const borrower = String(row[borrowerCol]).toLowerCase().trim();
            if (seenBorrowers.has(borrower)) {
                issues.duplicates.push({
                    value: row[borrowerCol],
                    rows: [seenBorrowers.get(borrower), index + 2],
                });
            } else {
                seenBorrowers.set(borrower, index + 2);
            }
        }
    });


    Object.entries(numericValues).forEach(([field, values]) => {
        if (values.length > 3) {
            const nums = values.map(v => v.value);
            const mean = nums.reduce((a, b) => a + b, 0) / nums.length;
            const std = Math.sqrt(nums.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / nums.length);

            values.forEach(({ value, row }) => {
                if (Math.abs(value - mean) > 3 * std) {
                    issues.outliers.push({ field, value, row, mean, std });
                }
            });
        }
    });

    return issues;
};
